import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { toast } from "react-toastify";
import { describeApiError } from "../../../api/client";
import { attachOpsDoc, detachOpsDoc, listBookingDocs, listOpsDocs } from "../../../api/opsDocsApi";
import "../Sops/sops.css";
import "./documents.css";

/* Documents that ride with one booking (a quote today; job/appointment use the same API).
   Attaching renders the document's {{merge.fields}} from this booking and keeps that copy —
   later template edits don't rewrite what went with this client. Fields the booking doesn't
   know are listed so a person fills them; nothing is invented. */
export default function BookingDocumentsPanel({ quoteId, jobId, appointmentId }) {
  const target = quoteId ? { quote: quoteId } : jobId ? { job: jobId } : appointmentId ? { appointment: appointmentId } : null;
  const targetKey = JSON.stringify(target);
  const [rows, setRows] = useState([]);
  const [docs, setDocs] = useState([]);
  const [pick, setPick] = useState("");
  const [openId, setOpenId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!target) return;
    try {
      const [a, d] = await Promise.all([listBookingDocs(target), listOpsDocs()]);
      setRows(a.data.attachments || []);
      setDocs(d.data.documents || []);
      setError(null);
    } catch (e) {
      setError(describeApiError(e, "Couldn't load documents"));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetKey]);
  useEffect(() => { load(); }, [load]);

  if (!target) return null;

  const attach = async () => {
    if (!pick) return;
    setBusy(true);
    try {
      const r = await attachOpsDoc(pick, target);
      setPick("");
      setOpenId(r.data.id);
      await load();
      toast.success(`${r.data.document.title} attached`);
    } catch (e) {
      toast.error(describeApiError(e, "Couldn't attach it"));
    } finally {
      setBusy(false);
    }
  };

  const detach = async (id) => {
    setBusy(true);
    try {
      await detachOpsDoc(id);
      await load();
    } catch (e) {
      toast.error(describeApiError(e, "Couldn't remove it"));
    } finally {
      setBusy(false);
    }
  };

  // only a confirmed document goes with a client: its questions are answered and every
  // counsel-flagged section has been confirmed by a person (the server enforces it too)
  const attachable = docs.filter((d) => d.status === "confirmed");
  const waiting = docs.filter((d) => d.status !== "confirmed" && d.status !== "archived").length;

  return (
    <div className="bkdoc">
      {error ? <div className="ppl-note danger">{error}</div> : null}
      {rows.length === 0 ? <p className="bkdoc-meta">No contract, waiver or checklist on this booking yet.</p> : (
        <div className="bkdoc-list">
          {rows.map((a) => (
            <div className="bkdoc-row" key={a.id}>
              <div className="bkdoc-row-head">
                <div>
                  <div className="bkdoc-title">{a.document.title}</div>
                  <div className="bkdoc-meta">Attached {new Date(a.created_at).toLocaleDateString()}{a.attached_by ? ` by ${a.attached_by}` : ""}</div>
                </div>
                <div style={{ display: "flex", gap: 6 }}>
                  <button className="bkdoc-btn" onClick={() => setOpenId(openId === a.id ? null : a.id)}>{openId === a.id ? "Hide" : "View"}</button>
                  <button className="bkdoc-btn" disabled={busy} onClick={() => detach(a.id)}>Remove</button>
                </div>
              </div>
              {a.unfilled_fields?.length ? (
                <div className="bkdoc-unfilled">Still to fill in: {a.unfilled_fields.join(", ")}</div>
              ) : null}
              {openId === a.id ? (
                <div className="bkdoc-body sop-md"><ReactMarkdown remarkPlugins={[remarkGfm]}>{a.rendered_body}</ReactMarkdown></div>
              ) : null}
            </div>
          ))}
        </div>
      )}
      <div className="bkdoc-attach">
        {attachable.length === 0 ? (
          <span className="bkdoc-meta">{waiting
            ? <>No confirmed documents yet ({waiting} still in review). <Link to="/operations/documents">Finish them in Operations → Documents</Link>.</>
            : <>No documents yet. <Link to="/operations/documents">Add one in Operations → Documents</Link>.</>}</span>
        ) : (
          <>
            <select value={pick} onChange={(e) => setPick(e.target.value)} aria-label="Document to attach">
              <option value="">Attach a document…</option>
              {attachable.map((d) => <option key={d.id} value={d.id}>{d.title}</option>)}
            </select>
            <button className="bkdoc-btn" disabled={!pick || busy} onClick={attach}>{busy ? "Attaching…" : "Attach"}</button>
          </>
        )}
      </div>
    </div>
  );
}
